<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.users.user_dialog.title_edit') }}
    </h1>
    <div v-loading="!isDataLoaded">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-items-center tw-gap-2">
          <div>
            <i-user-avatar
              :avatar="userAvatar && userAvatar['128'] && `${$baseHost}/${userAvatar['128']}`"
              :fullname="user.fullname"
              size="small"
              is-editable
              @click="$refs.avatarFile.click()"
            />
            <input
              ref="avatarFile"
              accept="image/*"
              class="tw-hidden"
              type="file"
              @change="uploadAvatar($event.target.files[0])"
            >
          </div>
          <i-form-item
            :label="$t('common.fields.first_name')"
            :error="errors?.first_name?.[0]"
            :class="form.first_name ? 'has-value' : ''"
            prop="first_name"
          >
            <el-input
              v-model="form.first_name"
            />
          </i-form-item>
          <i-form-item
            :label="$t('common.fields.last_name')"
            :error="errors?.last_name?.[0]"
            :class="form.last_name ? 'has-value' : ''"
            prop="last_name"
          >
            <el-input
              v-model="form.last_name"
            />
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.email')"
            :error="errors?.email?.[0]"
            :class="form.email ? 'has-value' : ''"
            prop="email"
          >
            <el-input
              v-model="form.email"
              type="email"
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.gender')"
            :class="form.gender ? 'has-value' : ''"
            prop="gender"
          >
            <el-select
              v-model="form.gender"
              filterable
              placeholder=""
            >
              <el-option
                v-for="item in genders"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </i-form-item>
          <i-form-item
            :label="$t('common.fields.role')"
            :class="form.role ? 'has-value' : ''"
            prop="role"
          >
            <el-select
              v-model="form.role"
              filterable
              placeholder=""
            >
              <el-option
                v-for="item in internalUserRoles"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </i-form-item>
        </div>

        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.phone')"
            :error="errors?.phone_primary?.[0]"
            :class="form.phone_primary ? 'has-value' : ''"
            prop="phone_primary"
          >
            <el-input
              v-model="form.phone_primary"
              type="tel"
            />
          </i-form-item>

          <i-form-item
            :label="$t('common.fields.phone')"
            :error="errors?.phone_secondary?.[0]"
            :class="form.phone_secondary ? 'has-value' : ''"
            prop="phone_secondary"
          >
            <el-input
              v-model="form.phone_secondary"
              type="tel"
            />
          </i-form-item>
        </div>

        <div class="tw-flex">
          <el-form-item>
            <el-switch
              v-model="form.is_active"
              :active-text="$t('common.filters.active')"
              class="tw-mt-2"
            />
          </el-form-item>
        </div>
      </el-form>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-between tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          type="white"
          :disabled="isPasswordAlreadyReset"
          @click="resetPassword"
        >
          {{ $t('adm.users.user_dialog.reset_password') }}
        </i-button>
        <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
          <i-button
            class="tw-w-40"
            @click="$emit('close')"
          >
            {{ $t('common.actions.cancel') }}
          </i-button>
          <i-button
            type="primary"
            class="tw-w-40"
            :is-loading="isLoading"
            @click="submitForm"
          >
            {{ $t('common.actions.edit') }}
          </i-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { deepDiff, getValidationError } from '@/helpers';
import AuthService from '@/services/AuthService';

const FORM_BASE = {
  first_name: '', // required, maxLength: 30
  last_name: '', //  maxLength: 150
  email: '',// required
  is_active: false,
  gender: '',// required
  role: '',// required
  phone_primary: '', // not required, but if entered maxLength: 32
  phone_secondary: '', // not required, but if entered maxLength: 32
};

export default {
  name: 'EditInternalUserDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isDataLoaded: false,
      isPasswordAlreadyReset: false,
      user: {},
      userAvatar: null,
      errors: {},
      form: _cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        first_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 30, message: 'Max 30 characters', trigger: 'blur' },
        ],
        last_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 150, message: 'Max 150 characters', trigger: 'blur' },
        ],
        email: [
          { required: true, message: 'Required', trigger: 'blur' },
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' },
        ],
        gender: [
          { required: true, message: 'Required', trigger: ['change', 'blur']  },
        ],
        role: [
          { required: true, message: 'Required', trigger: ['change', 'blur']  },
        ],
        phone_primary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
        phone_secondary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      genders: (state) => state.v2.shared.enums.enums.genders,
      internalUserRoles:(state) => state.v2.shared.enums.internal_user_roles,
    }),
  },

  watch: {
    user() {
      this.setInitialFormData();
    },
    isOpen(val) {
      if (val) {
        this.loadUserDetails();
      }
    },
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.$refs.Form?.clearValidate();
    },

    async loadUserDetails() {
      this.isDataLoaded = false;
      this.user = await this.$store.dispatch('v2/admin/cms/internal/users/getUserDetails', {
        id: this.userId,
      });
      this.isDataLoaded = true;
    },

    setInitialFormData() {
      const companyKeys = Object.keys(this.user);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (companyKeys.includes(key)) {
          this.form[key] = this.user[key];
          this.initialForm[key] = this.user[key];
        }
      });

      this.userAvatar = this.user.avatar_url;

      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/internal/users/editUser', {
          id: this.user.id,
          user: deepDiff(this.initialForm, this.form),
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },

    async uploadAvatar(file) {
      try {
        const response = await this.$store.dispatch('v2/admin/cms/internal/users/uploadUserAvatar', {
          id: this.user.id,
          avatar: file,
        });

        this.$message.success({
          message: this.$capitalize(_get(response, 'data.message') || 'Success'),
          showClose: true,
        });

        await this.loadUserDetails();
      } catch (error) {
        const errorText = getValidationError(error);

        this.$message.error({
          message: errorText,
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
    },

    async resetPassword() {
      try {
        await AuthService.resetUserPassword(this.user.id);
        this.isPasswordAlreadyReset = true;
      } catch (error) {
        const errorText = getValidationError(error);

        this.$message.error({
          message: errorText,
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
    },
  },
};
</script>
